import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import {
  AlertTriangle,
  BookOpen,
  Calendar,
  FileCheck,
  Plus,
  Save,
  Trash2,
  Users,
} from 'lucide-react'
import { Badge, Button, Card } from '@/components/ui'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { SearchableSelect } from '@/components/pedagogy/SearchableSelect'
import {
  addFranchiseTeacherAssignment,
  addFranchiseTeacherContract,
  closeTeacherTrainingCycle,
  deleteFranchiseTeacherAssignment,
  dismissFranchiseTeacherFromPartner,
  listTeacherRegistryReferenceData,
  saveTeacherTrainingAttestation,
  setFranchiseTeacherContractStatus,
  startFranchiseTeacherTraining,
  updateFranchiseTeacherAssignment,
  type FranchiseTeacherDetail,
  type FranchiseTeacherStatus,
  type TeacherAttestationResult,
  type TeacherRegistryReferenceData,
} from '@/services/franchiseTeacherService'

type SheetName = 'assignments' | 'dismiss-partner' | 'delete-assignment' | 'training' | 'attestation' | 'close-training' | 'contract' | null
type AssignmentSheetMode = 'add' | 'edit' | 'edit-single'

type AssignmentDraft = {
  id: string
  partnerNumber: string
  cityId: string
  startsOn: string
}

const statusLabels: Record<FranchiseTeacherStatus, string> = {
  candidate: 'Кандидат',
  training: 'В обучении',
  working: 'Работает',
  dismissed: 'Уволен',
}

const resultLabels: Record<TeacherAttestationResult, string> = {
  passed: 'Пройдена',
  retake: 'Пересдача',
  refused: 'Отказ',
}

function localIsoDate(): string {
  const now = new Date()
  const offset = now.getTimezoneOffset() * 60_000
  return new Date(now.getTime() - offset).toISOString().slice(0, 10)
}

function newAssignment(): AssignmentDraft {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    partnerNumber: '',
    cityId: '',
    startsOn: localIsoDate(),
  }
}

function formatDate(value: string): string {
  if (!value) return '—'
  const date = new Date(value.length > 10 ? value : `${value}T00:00:00`)
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('ru-RU').format(date)
}

export interface TeacherLifecyclePanelHandle {
  openAssignments: () => void
  openEditAssignments: () => void
  openEditAssignment: (assignmentId: string) => void
  openStatus: () => void
  openTraining: () => void
  openContract: (partnerNumber?: number) => void
  openAttestation: (stage: 1 | 2 | 3) => void
  openCloseTraining: () => void
  openDismissPartner: (partnerNumber: number) => void
  openDeleteAssignment: (assignmentId: string) => void
  terminateContract: (contractId: string) => void
}

export interface TeacherLifecyclePanelProps {
  teacher: FranchiseTeacherDetail
  onChanged: () => Promise<void> | void
  compact?: boolean
  showHistory?: boolean
  showPanel?: boolean
  /** Уже загруженный вызывающим справочник франчайзи — панель тогда не грузит его повторно. */
  referenceData?: TeacherRegistryReferenceData | null
}

export const TeacherLifecyclePanel = forwardRef<TeacherLifecyclePanelHandle, TeacherLifecyclePanelProps>(function TeacherLifecyclePanel({
  teacher,
  onChanged,
  compact = false,
  showHistory = true,
  showPanel = true,
  referenceData: providedReferenceData = null,
}, ref) {
  const [sheet, setSheet] = useState<SheetName>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [referenceData, setReferenceData] = useState<TeacherRegistryReferenceData | null>(providedReferenceData)
  const [referenceLoading, setReferenceLoading] = useState(false)

  const [assignments, setAssignments] = useState<AssignmentDraft[]>([])
  const [assignmentSheetMode, setAssignmentSheetMode] = useState<AssignmentSheetMode>('add')
  const [selectedPartnerNumber, setSelectedPartnerNumber] = useState<number | null>(null)
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null)
  const [actionReason, setActionReason] = useState('')

  const approvedVideos = useMemo(
    () => teacher.videoPresentations.filter(video => video.decision === 'approved'),
    [teacher.videoPresentations]
  )
  const [trainingDate, setTrainingDate] = useState(localIsoDate())
  const [trainingVideoId, setTrainingVideoId] = useState('')

  const [attestationMode, setAttestationMode] = useState<'plan' | 'result'>('plan')
  const [attestationStage, setAttestationStage] = useState<1 | 2 | 3>(1)
  const [attestationDate, setAttestationDate] = useState(localIsoDate())
  const [attestationResult, setAttestationResult] = useState<TeacherAttestationResult | null>(null)
  const [attestationPassedLocked, setAttestationPassedLocked] = useState(false)
  const [closeReason, setCloseReason] = useState('')

  const [contractPartnerNumber, setContractPartnerNumber] = useState('')
  const [contractDate, setContractDate] = useState(localIsoDate())
  const [contractUrl, setContractUrl] = useState('')

  const activePartnerOptions = useMemo(() => {
    const map = new Map<number, string>()
    for (const assignment of teacher.assignments) {
      map.set(assignment.partnerNumber, assignment.partnerName)
    }
    return Array.from(map.entries()).map(([partnerNumber, partnerName]) => ({ partnerNumber, partnerName }))
  }, [teacher.assignments])

  const activeContractWillBeReplaced = useMemo(() => (
    teacher.activeContracts.some(contract => (
      contractPartnerNumber
        ? String(contract.partnerNumber) === contractPartnerNumber
        : contract.partnerNumber == null
    ))
  ), [contractPartnerNumber, teacher.activeContracts])

  const selectedPartnerAssignments = useMemo(
    () => teacher.assignments.filter(assignment => assignment.partnerNumber === selectedPartnerNumber),
    [selectedPartnerNumber, teacher.assignments]
  )

  const selectedPartnerName = selectedPartnerAssignments[0]?.partnerName ?? 'выбранный франчайзи'
  const selectedPartnerContracts = useMemo(
    () => teacher.contracts.filter(contract => contract.partnerNumber === selectedPartnerNumber),
    [selectedPartnerNumber, teacher.contracts]
  )
  const isLastActivePartner = selectedPartnerNumber != null
    && teacher.assignments.every(assignment => assignment.partnerNumber === selectedPartnerNumber)

  const selectedAssignment = useMemo(
    () => teacher.assignments.find(assignment => assignment.assignmentId === selectedAssignmentId) ?? null,
    [selectedAssignmentId, teacher.assignments]
  )

  const selectedAssignmentContracts = useMemo(
    () => selectedAssignment
      ? teacher.contracts.filter(contract => contract.partnerNumber === selectedAssignment.partnerNumber)
      : [],
    [selectedAssignment, teacher.contracts]
  )

  const canEditTeacher = teacher.canManage && !teacher.archivedAt

  useEffect(() => {
    if (providedReferenceData) setReferenceData(providedReferenceData)
  }, [providedReferenceData])

  useEffect(() => {
    if (sheet !== 'assignments' || referenceData || referenceLoading) return
    setReferenceLoading(true)
    listTeacherRegistryReferenceData()
      .then(setReferenceData)
      .catch(loadError => setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить справочник'))
      .finally(() => setReferenceLoading(false))
  }, [sheet, referenceData, referenceLoading])

  const runAction = async (action: () => Promise<unknown>, closeAfter = true) => {
    setBusy(true)
    setError('')
    try {
      await action()
      if (closeAfter) setSheet(null)
      await onChanged()
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : 'Не удалось выполнить действие')
    } finally {
      setBusy(false)
    }
  }

  const openAssignments = () => {
    setAssignmentSheetMode('add')
    setAssignments([newAssignment()])
    setError('')
    setSheet('assignments')
  }

  const openEditAssignments = () => {
    setAssignmentSheetMode('edit')
    setAssignments(teacher.assignments.map(assignment => ({
      id: assignment.assignmentId,
      partnerNumber: String(assignment.partnerNumber),
      cityId: String(assignment.cityId),
      startsOn: assignment.startsOn ?? '',
    })))
    setError('')
    setSheet('assignments')
  }

  const openEditAssignment = (assignmentId: string) => {
    const assignment = teacher.assignments.find(item => item.assignmentId === assignmentId)
    if (!assignment) return
    setAssignmentSheetMode('edit-single')
    setAssignments([{
      id: assignment.assignmentId,
      partnerNumber: String(assignment.partnerNumber),
      cityId: String(assignment.cityId),
      startsOn: assignment.startsOn ?? '',
    }])
    setError('')
    setSheet('assignments')
  }

  const updateAssignment = (id: string, patch: Partial<AssignmentDraft>) => {
    setAssignments(current => current.map(item => {
      if (item.id !== id) return item
      const next = { ...item, ...patch }

      if (patch.cityId !== undefined) {
        const partnerOptions = (referenceData?.assignmentOptions ?? [])
          .filter(option => String(option.cityId) === patch.cityId)
        const uniquePartnerNumbers = Array.from(new Set(partnerOptions.map(option => String(option.partnerNumber))))
        const currentPartnerStillAllowed = uniquePartnerNumbers.includes(item.partnerNumber)
        if (!currentPartnerStillAllowed) {
          next.partnerNumber = uniquePartnerNumbers.length === 1 ? uniquePartnerNumbers[0] : ''
        }
      }

      return next
    }))
  }

  const saveAssignments = () => {
    if (assignments.length === 0 || assignments.some(item => !item.partnerNumber || !item.cityId)) {
      setError('Заполните франчайзи и город во всех строках')
      return
    }
    const keys = assignments.map(item => `${item.partnerNumber}:${item.cityId}`)
    if (new Set(keys).size !== keys.length) {
      setError('Одинаковая связь добавлена несколько раз')
      return
    }
    if (assignmentSheetMode === 'edit') {
      void runAction(() => Promise.all(assignments.map(item => updateFranchiseTeacherAssignment(item.id, {
        startsOn: item.startsOn,
      }))))
      return
    }
    if (assignmentSheetMode === 'edit-single') {
      void runAction(() => updateFranchiseTeacherAssignment(assignments[0].id, {
        startsOn: assignments[0].startsOn,
      }))
      return
    }
    void runAction(() => Promise.all(assignments.map(item => addFranchiseTeacherAssignment(
      teacher.teacherId,
      {
        partnerNumber: Number(item.partnerNumber),
        cityId: Number(item.cityId),
        startsOn: item.startsOn,
      }
    ))))
  }

  const openStatus = () => {
    openAssignments()
  }

  const openTraining = () => {
    setTrainingDate(localIsoDate())
    setTrainingVideoId(approvedVideos[0]?.videoPresentationId ?? '')
    setError('')
    setSheet('training')
  }

  const openAttestation = (stage: 1 | 2 | 3) => {
    const cycle = teacher.currentTraining
    if (!cycle) return
    const dates = [cycle.firstAttestationDate, cycle.secondAttestationDate, cycle.thirdAttestationDate]
    const results = [cycle.firstAttestationResult, cycle.secondAttestationResult, cycle.thirdAttestationResult]
    setAttestationStage(stage)
    setAttestationDate(dates[stage - 1] || localIsoDate())
    const currentResult = results[stage - 1]
    setAttestationMode(currentResult === 'retake' ? 'plan' : currentResult ? 'result' : 'plan')
    setAttestationResult(currentResult === 'retake' ? null : currentResult)
    setAttestationPassedLocked(currentResult === 'passed')
    setError('')
    setSheet('attestation')
  }

  const openContract = (partnerNumber?: number) => {
    const preferred = partnerNumber != null && activePartnerOptions.some(option => option.partnerNumber === partnerNumber)
      ? partnerNumber
      : activePartnerOptions[0]?.partnerNumber
    setContractPartnerNumber(preferred == null ? '' : String(preferred))
    setContractDate(localIsoDate())
    setContractUrl('')
    setError('')
    setSheet('contract')
  }

  const openDismissPartner = (partnerNumber: number) => {
    setSelectedPartnerNumber(partnerNumber)
    setActionReason('')
    setError('')
    setSheet('dismiss-partner')
  }

  const openDeleteAssignment = (assignmentId: string) => {
    setSelectedAssignmentId(assignmentId)
    setActionReason('')
    setError('')
    setSheet('delete-assignment')
  }

  const terminateContract = (contractId: string) => {
    if (!window.confirm('Расторгнуть этот договор?')) return
    void runAction(() => setFranchiseTeacherContractStatus(contractId, 'terminated'), false)
  }

  const saveContract = () => {
    if (!contractPartnerNumber) {
      setError('Выберите франчайзи для договора')
      return
    }
    if (activeContractWillBeReplaced && !window.confirm(
      'У этого франчайзи уже есть действующий договор. После сохранения предыдущий станет неактивным и останется в истории со статусом «Заменён». Продолжить?'
    )) return

    void runAction(() => addFranchiseTeacherContract(teacher.teacherId, {
      partnerNumber: contractPartnerNumber ? Number(contractPartnerNumber) : null,
      signedOn: contractDate,
      contractUrl,
    }))
  }

  const currentTraining = teacher.currentTraining
  const stage2Enabled = currentTraining?.firstAttestationResult === 'passed'
  const stage3Enabled = currentTraining?.secondAttestationResult === 'passed'

  const openCloseTraining = () => {
    setCloseReason('')
    setError('')
    setSheet('close-training')
  }

  const saveAttestation = () => {
    if (!currentTraining) return
    if (attestationMode === 'result' && attestationResult === 'refused' && !window.confirm(
      'Зафиксировать отказ? Текущее обучение будет закрыто, а педагог перейдёт в статус «Кандидат».'
    )) return

    void runAction(() => saveTeacherTrainingAttestation({
      trainingCycleId: currentTraining.trainingCycleId,
      stage: attestationStage,
      attestationDate,
      result: attestationMode === 'plan' ? null : attestationResult,
    }))
  }

  const saveDismissPartner = () => {
    if (selectedPartnerNumber == null) return
    if (isLastActivePartner && !window.confirm(
      `Это последняя активная связь педагога. После увольнения у франчайзи «${selectedPartnerName}» педагог перейдёт в статус «Уволен». Продолжить?`
    )) return
    void runAction(() => dismissFranchiseTeacherFromPartner(teacher.teacherId, selectedPartnerNumber, actionReason))
  }

  const saveDeleteAssignment = () => {
    if (!selectedAssignmentId) return
    const contractWarning = selectedAssignmentContracts.length > 0
      ? `\n\nУ франчайзи этой связи есть договоры: ${selectedAssignmentContracts.length}. Они не будут удалены молча; если связь действительно ошибочная, проверьте договоры отдельно.`
      : ''
    if (!window.confirm(`Удалить ошибочно созданную связь? Это не увольнение и не закрывает рабочий стаж.${contractWarning}`)) return
    void runAction(() => deleteFranchiseTeacherAssignment(selectedAssignmentId, actionReason))
  }

  useImperativeHandle(ref, () => ({
    openAssignments,
    openEditAssignments,
    openEditAssignment,
    openStatus,
    openTraining,
    openContract,
    openAttestation,
    openCloseTraining,
    openDismissPartner,
    openDeleteAssignment,
    terminateContract,
  }))

  return (
    <>
      {showPanel && (
      <Card>
        <div className="mb-3 flex items-center gap-2">
          <Calendar size={19} className="text-primary" />
          <h2 className="font-semibold">{compact ? 'Быстрые действия' : 'Управление карточкой'}</h2>
        </div>

        {error && sheet == null && (
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-error/30 bg-error/5 p-3 text-sm text-error">
            <AlertTriangle size={17} /> {error}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={openAssignments} disabled={Boolean(teacher.archivedAt)}>
            <Users size={16} className="mr-1.5 inline" />Добавить связь
          </Button>
          {!currentTraining && canEditTeacher && teacher.status !== 'dismissed' && (
            <Button size="sm" variant="outline" onClick={openTraining}>
              <BookOpen size={16} className="mr-1.5 inline" />
              {teacher.status === 'working' ? 'Повторное обучение' : 'Начать обучение'}
            </Button>
          )}
          {canEditTeacher && teacher.status !== 'dismissed' && (
            <Button size="sm" variant="outline" onClick={() => openContract()} disabled={teacher.assignments.length === 0}>
              <FileCheck size={16} className="mr-1.5 inline" />Добавить договор
            </Button>
          )}
        </div>

        {!canEditTeacher && !teacher.archivedAt && (
          <div className="mt-3 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
            Карточка доступна только для просмотра. Добавьте педагога к своему франчайзи, чтобы выполнять рабочие действия.
          </div>
        )}

        {!compact && currentTraining && (
          <div className="mt-4 rounded-xl border border-border p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-medium">Аттестации текущего обучения</div>
                <div className="mt-1 text-xs text-muted-foreground">Начало: {formatDate(currentTraining.startDate || '')}</div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  openCloseTraining()
                }}
              >
                Завершить досрочно
              </Button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => openAttestation(1)}>
                1-я: {currentTraining.firstAttestationResult ? resultLabels[currentTraining.firstAttestationResult] : currentTraining.firstAttestationDate ? 'запланирована' : 'заполнить'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => openAttestation(2)} disabled={!stage2Enabled}>
                2-я: {currentTraining.secondAttestationResult ? resultLabels[currentTraining.secondAttestationResult] : currentTraining.secondAttestationDate ? 'запланирована' : 'заполнить'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => openAttestation(3)} disabled={!stage3Enabled}>
                3-я: {currentTraining.thirdAttestationResult ? resultLabels[currentTraining.thirdAttestationResult] : currentTraining.thirdAttestationDate ? 'запланирована' : 'заполнить'}
              </Button>
            </div>
          </div>
        )}

        {!compact && teacher.activeContracts.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-sm font-medium">Действующие договоры</div>
            {teacher.activeContracts.map(contract => (
              <div key={contract.contractId} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2">
                <div className="text-sm">
                  {contract.partnerName || 'Без франчайзи'} · до {formatDate(contract.expiresOn)}
                </div>
                <Button size="sm" variant="outline" onClick={() => terminateContract(contract.contractId)} disabled={busy}>
                  Расторгнуть
                </Button>
              </div>
            ))}
          </div>
        )}

        {teacher.assignments.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-sm font-medium">Активные связи</div>
            {teacher.assignments.map(assignment => (
              <div key={assignment.assignmentId} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2">
                <div className="text-sm">
                  {assignment.partnerName} · {assignment.cityName} · с {formatDate(assignment.startsOn || '')}
                </div>
                {canEditTeacher && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openDismissPartner(assignment.partnerNumber)} disabled={busy}>
                      Уволить у франчайзи
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openDeleteAssignment(assignment.assignmentId)} disabled={busy}>
                      <Trash2 size={15} />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
      )}

      {showPanel && showHistory && teacher.statusHistory.length > 0 && (
        <Card>
          <div className="mb-3 flex items-center gap-2">
            <Calendar size={19} className="text-primary" />
            <h2 className="font-semibold">История статусов</h2>
            <Badge variant="neutral">{teacher.statusHistory.length}</Badge>
          </div>
          <div className="space-y-2">
            {teacher.statusHistory.map(item => (
              <div key={item.statusHistoryId} className="rounded-lg border border-border p-3">
                <div className="text-sm font-medium">
                  {item.fromStatus ? `${statusLabels[item.fromStatus]} → ` : ''}{statusLabels[item.toStatus]}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {formatDate(item.changedAt)}{item.reason ? ` · ${item.reason}` : ''}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/*
        Шторка шире дефолтных `sm:max-w-lg` (512 px) намеренно: строка связи —
        это три поля в ряд, а брейкпоинты Tailwind смотрят на ширину ОКНА, а не
        контейнера, поэтому в узкой шторке `md:grid-cols-…` раскладывалась в
        четыре колонки внутри 512 px и поля ужимались до переноса слов.
      */}
      <BottomSheet isOpen={sheet === 'assignments'} onClose={() => setSheet(null)} title={assignmentSheetMode === 'add' ? 'Добавить связь с франчайзи' : 'Изменить связь с франчайзи'} className="sm:max-w-4xl">
        <div className="space-y-4 p-4 sm:p-5">
          {error && <div className="rounded-lg border border-error/30 bg-error/5 p-3 text-sm text-error">{error}</div>}
          <p className="text-sm leading-6 text-muted-foreground">
            {assignmentSheetMode !== 'add'
              ? assignmentSheetMode === 'edit-single'
                ? 'Измените дату начала этой связи. Чтобы сменить франчайзи или город, закройте связь и добавьте новую.'
                : 'Измените даты начала действующих связей. Чтобы сменить франчайзи или город, закройте связь и добавьте новую.'
              : 'Свяжите педагога с франчайзи и городом, где он работает. Связей может быть несколько.'}
          </p>
          {teacher.status === 'dismissed' && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm leading-6 text-muted-foreground">
              Новая активная связь вернёт уволенного педагога в работу. Новая карточка не создаётся, история остаётся здесь.
            </div>
          )}
          {referenceLoading && <div className="text-sm text-muted-foreground">Загружаем справочник…</div>}
          <div className="space-y-3">
            {assignments.map((item, index) => {
              const partnerOptions = (referenceData?.assignmentOptions ?? [])
                .filter(option => String(option.cityId) === item.cityId)
                .reduce<Map<number, string>>((map, option) => {
                  map.set(option.partnerNumber, option.partnerName || `Франчайзи №${option.partnerNumber}`)
                  return map
                }, new Map())
              return (
                <div key={item.id} className="rounded-xl border border-border p-3 sm:p-4">
                  {assignments.length > 1 && (
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Связь {index + 1}</span>
                      <button
                        type="button"
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-sm text-error transition-colors hover:bg-error/10"
                        onClick={() => setAssignments(current => current.filter(row => row.id !== item.id))}
                      >
                        <Trash2 size={15} />Удалить
                      </button>
                    </div>
                  )}
                  {/*
                    `minmax(0, …)` и `min-w-0` обязательны: у грид-ячейки
                    min-width по умолчанию `auto`, поэтому широкий min-content
                    у `input[type=date]` растягивал строку за пределы шторки и
                    давал горизонтальный скролл всей модалки.
                  */}
                  <div className={assignmentSheetMode !== 'add' ? 'grid gap-3 md:grid-cols-[minmax(0,1fr)_180px]' : 'grid gap-3 sm:grid-cols-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_180px]'}>
                    {assignmentSheetMode !== 'add' ? (
                      <div className="min-w-0 rounded-lg bg-muted/40 px-3 py-2.5">
                        <span className="block text-xs font-medium text-muted-foreground">Текущая связь</span>
                        <div className="mt-1 text-sm font-medium text-foreground">
                          {teacher.assignments.find(assignment => assignment.assignmentId === item.id)?.partnerName ?? 'Франчайзи'}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {teacher.assignments.find(assignment => assignment.assignmentId === item.id)?.cityName ?? 'Город'}
                        </div>
                      </div>
                    ) : (
                      <label className="block min-w-0 space-y-1.5">
                        <span className="text-sm font-medium">Город</span>
                        <SearchableSelect
                          value={item.cityId}
                          onChange={value => updateAssignment(item.id, { cityId: value })}
                          placeholder="Выберите город"
                          searchPlaceholder="Поиск по городу"
                          options={(referenceData?.cities ?? []).map(option => ({ value: String(option.cityId), label: option.cityName }))}
                        />
                      </label>
                    )}
                    {assignmentSheetMode === 'add' && (
                      <label className="block min-w-0 space-y-1.5">
                        <span className="text-sm font-medium">Франчайзи</span>
                        <SearchableSelect
                          value={item.partnerNumber}
                          onChange={value => updateAssignment(item.id, { partnerNumber: value })}
                          placeholder={item.cityId ? 'Выберите франчайзи' : 'Сначала выберите город'}
                          searchPlaceholder="Поиск по имени или номеру"
                          disabled={!item.cityId}
                          options={Array.from(partnerOptions.entries())
                            .map(([partnerNumber, partnerName]) => ({
                              value: String(partnerNumber),
                              label: partnerName,
                              searchText: String(partnerNumber),
                            }))
                            .sort((a, b) => a.label.localeCompare(b.label, 'ru'))}
                        />
                      </label>
                    )}
                    <label className="block min-w-0 space-y-1.5 sm:col-span-2 md:col-span-1">
                      <span className="text-sm font-medium">Дата начала работы</span>
                      <input type="date" className="input w-full min-w-0" value={item.startsOn} onChange={event => updateAssignment(item.id, { startsOn: event.target.value })} />
                    </label>
                  </div>
                </div>
              )
            })}
          </div>
          {assignmentSheetMode === 'add' && (
            <Button size="sm" variant="outline" onClick={() => setAssignments(current => [...current, newAssignment()])}>
              <Plus size={16} className="mr-1.5 inline" />Ещё одна связь
            </Button>
          )}
          <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setSheet(null)} disabled={busy}>Отмена</Button>
            <Button onClick={saveAssignments} disabled={busy || referenceLoading}>
              <Save size={16} className="mr-1.5 inline" />{busy ? 'Сохраняем…' : 'Сохранить'}
            </Button>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet isOpen={sheet === 'dismiss-partner'} onClose={() => setSheet(null)} title="Увольнение у франчайзи">
        <div className="space-y-4 p-4">
          {error && <div className="rounded-lg border border-error/30 bg-error/5 p-3 text-sm text-error">{error}</div>}
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm leading-6 text-amber-900">
            {isLastActivePartner
              ? `Это последняя активная связь педагога. После увольнения у франчайзи «${selectedPartnerName}» педагог перейдёт в статус «Уволен».`
              : `Будет закрыта активная связь с франчайзи «${selectedPartnerName}». Остальные активные связи останутся рабочими, педагог не будет уволен полностью.`}
            {selectedPartnerContracts.length > 0 && (
              <span className="mt-1 block">
                Договоры этого франчайзи: {selectedPartnerContracts.length}. Закрытие договора выполняется текущей серверной lifecycle-логикой.
              </span>
            )}
          </div>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Причина</span>
            <textarea className="input min-h-24 w-full resize-y" value={actionReason} onChange={event => setActionReason(event.target.value)} />
          </label>
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button variant="outline" onClick={() => setSheet(null)} disabled={busy}>Отмена</Button>
            <Button
              onClick={saveDismissPartner}
              disabled={busy || selectedPartnerNumber == null}
            >
              {busy ? 'Сохраняем…' : 'Уволить у франчайзи'}
            </Button>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet isOpen={sheet === 'delete-assignment'} onClose={() => setSheet(null)} title="Удалить ошибочную связь">
        <div className="space-y-4 p-4">
          {error && <div className="rounded-lg border border-error/30 bg-error/5 p-3 text-sm text-error">{error}</div>}
          <div className="rounded-lg border border-error/30 bg-error/5 p-3 text-sm leading-6 text-error">
            Это действие только для ошибочно созданной связи. Рабочее увольнение выполняется через «Уволить у франчайзи».
            {selectedAssignment && (
              <span className="mt-1 block">
                Связь: {selectedAssignment.partnerName} · {selectedAssignment.cityName} · с {formatDate(selectedAssignment.startsOn || '')}.
              </span>
            )}
            {selectedAssignmentContracts.length > 0 && (
              <span className="mt-1 block">
                У франчайзи этой связи есть договоры: {selectedAssignmentContracts.length}. Они не удаляются молча; после удаления проверьте договорный блок.
              </span>
            )}
          </div>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Причина *</span>
            <textarea className="input min-h-24 w-full resize-y" value={actionReason} onChange={event => setActionReason(event.target.value)} />
          </label>
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button variant="outline" onClick={() => setSheet(null)} disabled={busy}>Отмена</Button>
            <Button
              onClick={saveDeleteAssignment}
              disabled={busy || !selectedAssignmentId || !actionReason.trim()}
            >
              {busy ? 'Удаляем…' : 'Удалить связь'}
            </Button>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet isOpen={sheet === 'training'} onClose={() => setSheet(null)} title={teacher.status === 'working' ? 'Повторное обучение' : 'Начать обучение'}>
        <div className="space-y-4 p-4">
          {error && <div className="rounded-lg border border-error/30 bg-error/5 p-3 text-sm text-error">{error}</div>}
          <label className="block space-y-1">
            <span className="text-sm font-medium">Дата начала</span>
            <input type="date" className="input w-full" value={trainingDate} onChange={event => setTrainingDate(event.target.value)} />
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Одобренная видеовизитка</span>
            <select className="input w-full" value={trainingVideoId} onChange={event => setTrainingVideoId(event.target.value)}>
              <option value="">Не выбрана</option>
              {approvedVideos.map(video => (
                <option key={video.videoPresentationId} value={video.videoPresentationId}>
                  {`Видеовизитка от ${formatDate(video.evaluatedAt)}${video.partnerName || video.cityName ? ` · ${[video.partnerName, video.cityName].filter(Boolean).join(' · ')}` : ''}`}
                </option>
              ))}
            </select>
          </label>
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm leading-6 text-muted-foreground">
            Одобрение видеовизитки само обучение не запускает. Для нового кандидата нужна одобренная визитка; для working-педагога повторное обучение можно запустить без неё.
          </div>
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button variant="outline" onClick={() => setSheet(null)} disabled={busy}>Отмена</Button>
            <Button
              onClick={() => void runAction(() => startFranchiseTeacherTraining(teacher.teacherId, {
                startDate: trainingDate,
                isRepeat: teacher.status === 'working',
                approvedVideoPresentationId: trainingVideoId || null,
              }))}
              disabled={busy || !trainingDate || (teacher.status !== 'working' && !trainingVideoId)}
            >
              {busy ? 'Запускаем…' : 'Запустить'}
            </Button>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet isOpen={sheet === 'attestation'} onClose={() => setSheet(null)} title={`Аттестация №${attestationStage}`}>
        <div className="space-y-4 p-4">
          {error && <div className="rounded-lg border border-error/30 bg-error/5 p-3 text-sm text-error">{error}</div>}
          <label className="block space-y-1">
            <span className="text-sm font-medium">Дата</span>
            <input type="date" className="input w-full" value={attestationDate} onChange={event => setAttestationDate(event.target.value)} />
          </label>
          <div className="grid grid-cols-2 gap-2 rounded-lg bg-muted p-1">
            <button
              type="button"
              disabled={attestationPassedLocked}
              onClick={() => { setAttestationMode('plan'); setAttestationResult(null) }}
              className={`rounded-md px-3 py-2 text-sm font-medium ${attestationMode === 'plan' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}
            >
              Запланировать
            </button>
            <button
              type="button"
              onClick={() => { setAttestationMode('result'); setAttestationResult(current => current ?? 'passed') }}
              className={`rounded-md px-3 py-2 text-sm font-medium ${attestationMode === 'result' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}
            >
              Зафиксировать результат
            </button>
          </div>
          {attestationMode === 'plan' ? (
            <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
              Сохранится только дата. Пока результат не указан, аттестация считается запланированной.
            </div>
          ) : (
            <label className="block space-y-1">
              <span className="text-sm font-medium">Результат</span>
              <select className="input w-full" value={attestationResult ?? ''} onChange={event => setAttestationResult(event.target.value as TeacherAttestationResult)} disabled={attestationPassedLocked}>
                <option value="passed">Сдано</option>
                <option value="retake">Пересдача</option>
                <option value="refused">Отказ</option>
              </select>
            </label>
          )}
          {attestationMode === 'result' && attestationResult === 'retake' && (
            <div className="text-sm text-muted-foreground">После сохранения назначьте новую дату этой аттестации через режим «Запланировать».</div>
          )}
          {attestationPassedLocked && (
            <div className="text-sm text-muted-foreground">Аттестация уже сдана. При необходимости можно исправить дату, но результат останется «Сдано».</div>
          )}
          {attestationMode === 'result' && attestationResult === 'refused' && (
            <div className="text-sm text-error">Отказ закроет текущий цикл обучения и вернёт педагога в статус «Кандидат».</div>
          )}
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button variant="outline" onClick={() => setSheet(null)} disabled={busy}>Отмена</Button>
            <Button
              onClick={saveAttestation}
              disabled={busy || !attestationDate || !currentTraining || (attestationMode === 'result' && !attestationResult)}
            >
              {busy ? 'Сохраняем…' : 'Сохранить'}
            </Button>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet isOpen={sheet === 'close-training'} onClose={() => setSheet(null)} title="Завершить досрочно">
        <div className="space-y-4 p-4">
          {error && <div className="rounded-lg border border-error/30 bg-error/5 p-3 text-sm text-error">{error}</div>}
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">Не все аттестации пройдены. Цикл будет отмечен как завершённый досрочно.</div>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Причина</span>
            <textarea className="input min-h-24 w-full resize-y" value={closeReason} onChange={event => setCloseReason(event.target.value)} />
          </label>
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button variant="outline" onClick={() => setSheet(null)} disabled={busy}>Отмена</Button>
            <Button
              onClick={() => currentTraining && void runAction(() => closeTeacherTrainingCycle(currentTraining.trainingCycleId, closeReason))}
              disabled={busy || !closeReason.trim() || !currentTraining}
            >
              {busy ? 'Закрываем…' : 'Закрыть'}
            </Button>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet isOpen={sheet === 'contract'} onClose={() => setSheet(null)} title="Новый договор">
        <div className="space-y-4 p-4">
          {error && <div className="rounded-lg border border-error/30 bg-error/5 p-3 text-sm text-error">{error}</div>}
          <label className="block space-y-1">
            <span className="text-sm font-medium">Франчайзи</span>
            <select className="input w-full" value={contractPartnerNumber} onChange={event => setContractPartnerNumber(event.target.value)}>
              <option value="">Выберите франчайзи</option>
              {activePartnerOptions.map(option => <option key={option.partnerNumber} value={option.partnerNumber}>{option.partnerName}</option>)}
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Дата подписания</span>
            <input type="date" className="input w-full" value={contractDate} onChange={event => setContractDate(event.target.value)} />
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Ссылка на договор</span>
            <input className="input w-full" value={contractUrl} onChange={event => setContractUrl(event.target.value)} placeholder="https://..." />
          </label>
          {activeContractWillBeReplaced && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              У этого франчайзи уже есть действующий договор. После добавления нового договора предыдущий останется в истории со статусом «Заменён».
            </div>
          )}
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button variant="outline" onClick={() => setSheet(null)} disabled={busy}>Отмена</Button>
            <Button
              onClick={saveContract}
              disabled={busy || !contractPartnerNumber || !contractDate || !contractUrl.trim()}
            >
              {busy ? 'Сохраняем…' : 'Добавить'}
            </Button>
          </div>
        </div>
      </BottomSheet>
    </>
  )
})
